<script setup>
import { computed } from 'vue'
import { format } from '@formkit/tempo'
import Tag from '@components/global/Tag.vue'
import Button from '@components/forms/Button.vue'
import EventCardTags from '@components/index/EventCardTags.vue'

const props = defineProps({
	event: {
		type: Object,
		required: true,
	},
	compact: {
		type: Boolean,
		default: false,
	},
	light: {
		type: Boolean,
		default: false,
	},
	card: {
		type: Boolean,
		default: false,
	},
})

const games = [
	{ slug: 'fatal-fury', name: 'Fatal Fury: COTW' },
	{ slug: 'king-of-fighters', name: 'KOF XV' },
	{ slug: 'samurai-shodown', name: 'Samurai Shodown' },
]

// The tier recorded against the game takes priority, since that is how the 2026
// season lists its qualifiers. Older events have no tier on their games, so they
// fall back to the external link, which is still how 2025 is listed. A game can
// be attached before its tier is settled, in which case it falls back too.
const buttonFor = (slug) => {
	const gameLink = (props.event.game_links ?? []).find((link) => link.game?.slug === slug)
	const tier = gameLink?.tournament_tier

	if (tier) {
		return {
			label: gameLink.highlighted ? `${tier.name} ★` : tier.name,
			url: gameLink.url,
			accent: gameLink.highlighted ? 'gold' : 'primary',
		}
	}

	const externalLink = (props.event.external_links ?? []).find((link) => link.game?.slug === slug)

	if (!externalLink?.label) {
		return null
	}

	return {
		label: externalLink.label,
		url: externalLink.url,
		accent: externalLink.accent_color,
	}
}

const buttons = computed(() => games.map((game) => ({ ...game, button: buttonFor(game.slug) })))

const accentClass = (accent) => (accent === 'primary' ? 'bg-snk-blue-500!' : accent === 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!')

const dateRange = (start, end) => {
	const startMonth = format({ date: start, format: 'MMM', tz: 'UTC' })
	const endMonth = format({ date: end, format: 'MMM', tz: 'UTC' })
	const startDay = format({ date: start, format: 'D', tz: 'UTC' })
	const endDay = format({ date: end, format: 'D', tz: 'UTC' })

	if (startMonth === endMonth && startDay === endDay) {
		return `${startMonth}. ${startDay}`
	} else if (startMonth === endMonth) {
		return `${startMonth}. ${startDay}-${endDay}`
	} else {
		return `${startMonth}. ${startDay} - ${endMonth}. ${endDay}`
	}
}
</script>

<template>
	<div class="flex flex-col justify-between gap-5 rounded-xl bg-white p-5 lg:flex-row">
		<div class="flex gap-5">
			<div class="bg-snk-blue-500 flex size-16 items-center justify-center overflow-hidden rounded-xl p-1" v-if="event.logo">
				<img class="h-full w-full object-contain" :src="event.logo" :alt="event.name" />
			</div>
			<div class="flex flex-col justify-center gap-2">
				<div class="flex flex-col items-start md:flex-row md:items-center md:gap-2">
					<div class="text-lg font-bold">{{ event.name }}</div>
					<div class="rounded-[.25rem] bg-[#EE7D00] p-1 text-[.6rem] leading-none font-bold text-nowrap text-white uppercase" v-if="event.ewc_slots">EWC {{ event.ewc_slots }} Slots</div>
				</div>
				<p class="text-sm text-neutral-400">
					{{ dateRange(event.start_at, event.finish_at) }}
					<span v-if="event.country?.name">, {{ event.country?.name }}</span>
				</p>
			</div>
		</div>
		<div class="flex flex-col gap-2 md:flex-row md:gap-5">
			<div class="flex flex-col gap-1" v-for="game in buttons" :key="game.slug">
				<div class="text-xs font-bold text-neutral-400 uppercase">{{ game.name }}</div>
				<a :class="['bg-snk-light-500 flex h-8 w-full items-center justify-center rounded-lg text-xs font-bold text-white uppercase transition-transform duration-300 ease-in-out md:h-10 md:w-40 md:hover:scale-105', accentClass(game.button.accent)]" :href="game.button.url" v-if="game.button?.url" target="_blank">
					{{ game.button.label }}
				</a>
				<div :class="['bg-snk-light-500 pointer-events-none flex h-8 w-full items-center justify-center rounded-lg text-xs font-bold text-white uppercase transition-transform duration-300 ease-in-out md:h-10 md:w-40', accentClass(game.button.accent)]" v-else-if="game.button">
					{{ game.button.label }}
				</div>
				<div class="bg-snk-light-500 h-10 w-full rounded-lg md:w-40" v-else />
			</div>
		</div>
	</div>
</template>
